import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { X } from 'lucide-react';

/* ============ Design Tokens ============ */
const tokens = {
  bgCard: '#1e1e2e',
  bgInset: '#0f172a',
  textPrimary: '#ffffff',
  textMuted: '#94a3b8',
  accentGradient: 'linear-gradient(135deg, #22d3ee 0%, #8b5cf6 100%)',
  accentGlow: '0 0 15px rgba(34, 211, 238, 0.5), 0 0 30px rgba(139, 92, 246, 0.3)',
  successBg: 'rgba(34, 197, 94, 0.2)',
  successText: '#4ade80',
  borderSubtle: '#334155',
  neonYellow: 'rgba(219, 255, 0, 0.45)',
  neonYellowStrong: 'rgba(219, 255, 0, 0.75)',
  tileBg: 'rgba(0, 0, 0, 0.30)',
  dangerBg: 'rgba(239, 68, 68, 0.15)',
  dangerText: '#ef4444',
};

/* ============ Animations ============ */
const pulseGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 15px rgba(34, 211, 238, 0.5), 0 0 30px rgba(139, 92, 246, 0.3);
  }
  50% { 
    box-shadow: 0 0 25px rgba(34, 211, 238, 0.7), 0 0 50px rgba(139, 92, 246, 0.5);
  }
`;

const scaleIn = keyframes`
  0% { transform: scale(0.8); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
`;

/* ============ Main Container ============ */
const SelectorCard = styled.div`
  background: transparent;
  border-radius: 16px;
  padding: 0.75rem;
  margin-bottom: 1.5rem;
`;

/* ============ Header ============ */
const Header = styled.div`
  display: none;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const HeaderLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${tokens.textMuted};
`;

const StatusPill = styled.div<{ $isFull: boolean }>`
  padding: 0.375rem 0.875rem;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.3s ease;
  
  ${props => props.$isFull ? css`
    background: ${tokens.successBg};
    color: ${tokens.successText};
    border: 1px solid rgba(34, 197, 94, 0.3);
  ` : css`
    background: rgba(255, 255, 255, 0.05);
    color: ${tokens.textMuted};
    border: 1px solid ${tokens.borderSubtle};
  `}
`;

/* ============ Number Grid ============ */
const NumberGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(9, 1fr);
  gap: 12px;
  margin-bottom: 0;
  max-width: 920px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 600px) {
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
  }
`;

const NumberCircle = styled.button<{ $selected: boolean }>`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;
  position: relative;
  outline: none;
  background: rgba(0, 0, 0, 0.42);
  color: ${tokens.textPrimary};
  border: 1px solid rgba(219, 255, 0, 0.4);

  ${props => props.$selected && css`
    background: linear-gradient(
      135deg,
      rgba(219, 255, 0, 0.62) 0%,
      rgba(219, 255, 0, 0.28) 55%,
      rgba(0, 0, 0, 0.30) 100%
    );
    border-color: rgba(219, 255, 0, 0.95);
    color: rgba(8, 10, 20, 0.96);
    font-weight: 800;
    box-shadow:
      0 0 0 1px rgba(219, 255, 0, 0.35),
      0 0 34px rgba(219, 255, 0, 0.28),
      0 0 70px rgba(219, 255, 0, 0.12),
      inset 0 0 0 1px rgba(219, 255, 0, 0.28),
      inset 0 0 18px rgba(219, 255, 0, 0.35);
    text-shadow: none;
  `}

  &:hover:not(:disabled) {
    border-color: rgba(219, 255, 0, 0.75);
    ${props => props.$selected
      ? css`
          background: linear-gradient(
            135deg,
            rgba(219, 255, 0, 0.70) 0%,
            rgba(219, 255, 0, 0.32) 55%,
            rgba(0, 0, 0, 0.28) 100%
          );
        `
      : css`
          background: rgba(0, 0, 0, 0.48);
        `}
    transform: translateY(-1px);
    box-shadow: 0 0 0 1px rgba(219, 255, 0, 0.12), 0 0 18px rgba(219, 255, 0, 0.10);
  }

  &:hover:not(:disabled) {
    ${props => props.$selected && css`
      border-color: rgba(219, 255, 0, 1);
      box-shadow:
        0 0 0 1px rgba(219, 255, 0, 0.45),
        0 0 42px rgba(219, 255, 0, 0.32),
        0 0 90px rgba(219, 255, 0, 0.14),
        inset 0 0 0 1px rgba(219, 255, 0, 0.22);
    `}
  }

  &:active:not(:disabled) {
    transform: translateY(0px);
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

/* ============ Footer / Summary Bar ============ */
const SummaryBar = styled.div`
  display: none;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${tokens.bgInset};
  border-radius: 12px;
  border: 1px solid ${tokens.borderSubtle};
`;

const SummaryLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${tokens.textMuted};
  white-space: nowrap;
`;

const SelectedNumbersRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex: 1;
  flex-wrap: wrap;
`;

const MiniNumber = styled.div<{ $hasValue: boolean }>`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-size: 13px;
  font-weight: 700;
  transition: all 0.2s ease;
  animation: ${scaleIn} 0.2s ease-out;

  ${props => props.$hasValue ? css`
    background: ${tokens.accentGradient};
    color: #000;
    box-shadow: 0 0 10px rgba(34, 211, 238, 0.4);
  ` : css`
    background: transparent;
    border: 2px dashed ${tokens.borderSubtle};
    color: ${tokens.textMuted};
  `}
`;

const ClearButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: none;
  background: ${tokens.dangerBg};
  color: ${tokens.dangerText};
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: rgba(239, 68, 68, 0.25);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const GridContainer = styled.div<{ $isDisabled: boolean }>`
  position: relative;
  
  ${props => props.$isDisabled && css`
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.4);
      border-radius: 16px;
      pointer-events: none;
      z-index: 10;
    }
  `}
`;

interface LotteryNumberSelectorProps {
  lotteryDetails: {
    maxNum: number;
  };
  maxNumbers: number;
  onNumbersSelected: (numbers: number[]) => void;
  preselectedNumbers?: number[];
  isLoading?: boolean;
  lotteryState?: number;
  timeRemaining?: number;
}

// Using React.memo to prevent re-renders if props haven't changed
export const LotteryNumberSelector = React.memo(
  ({
    lotteryDetails,
    maxNumbers,
    onNumbersSelected,
    preselectedNumbers = [],
    isLoading = false,
    lotteryState = 1,
    timeRemaining = 0,
  }: LotteryNumberSelectorProps) => {
    const { maxNum } = lotteryDetails;

    // Using useMemo to create a stable array of numbers for rendering
    const numberRange = useMemo(
      () => Array.from({ length: maxNum }, (_, i) => i + 1),
      [maxNum]
    );

    const [selectedNumbers, setSelectedNumbers] = useState(preselectedNumbers);

    // Using useCallback to memoize the handler and prevent unnecessary re-renders in child components
    const handleNumberSelect = useCallback(
      (number: number) => {
        setSelectedNumbers(prevSelected => {
          const isSelected = prevSelected.includes(number);

          if (process.env.NODE_ENV === 'development') {
            console.debug('[LotteryNumberSelector] handleNumberSelect', {
              number,
              isSelected,
              prevCount: prevSelected.length,
              maxNumbers,
            });
          }

          if (isSelected) {
            return prevSelected.filter(n => n !== number);
          }

          if (prevSelected.length < maxNumbers) {
            const newSelection = [...prevSelected, number];
            newSelection.sort((a, b) => a - b);
            return newSelection;
          }

          return prevSelected; // No change needed
        });
      },
      [maxNumbers]
    );

    useEffect(() => {
      // Update parent component when selectedNumbers change
      onNumbersSelected(selectedNumbers);
    }, [selectedNumbers, onNumbersSelected]);

    useEffect(() => {
      // Update local state only if preselectedNumbers have actually changed
      const isPreselectedChanged =
        preselectedNumbers.length !== selectedNumbers.length ||
        preselectedNumbers.some((num, index) => num !== selectedNumbers[index]);

      if (isPreselectedChanged) {
        setSelectedNumbers(preselectedNumbers);
      }
    }, [preselectedNumbers]);

    const isLotteryActive = lotteryState === 1 && timeRemaining > 15;
    const isDisabled = isLoading || !isLotteryActive;

    useEffect(() => {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[LotteryNumberSelector]', {
          maxNum,
          maxNumbers,
          preselectedCount: preselectedNumbers.length,
          isDisabled,
        });
      }
    }, [maxNum, maxNumbers, preselectedNumbers.length, isDisabled]);

    const handleClear = () => {
      setSelectedNumbers([]);
    };

    // Create preview slots for the summary bar
    const previewSlots = Array.from({ length: maxNumbers }, (_, i) => 
      selectedNumbers[i] ?? null
    );

    return (
      <GridContainer $isDisabled={isDisabled}>
        <SelectorCard>
          {/* Header */}
          <Header>
            <HeaderLabel>Select Your Numbers</HeaderLabel>
            <StatusPill $isFull={selectedNumbers.length === maxNumbers}>
              {selectedNumbers.length} / {maxNumbers} selected
            </StatusPill>
          </Header>

          {/* Number Grid */}
          <NumberGrid>
            {numberRange.map(number => (
              <NumberCircle
                key={number}
                $selected={selectedNumbers.includes(number)}
                onClick={() => !isDisabled && handleNumberSelect(number)}
                disabled={isDisabled}
              >
                {number}
              </NumberCircle>
            ))}
          </NumberGrid>

          {/* Summary Footer */}
          <SummaryBar>
            <SummaryLabel>Your Numbers</SummaryLabel>
            <SelectedNumbersRow>
              {previewSlots.map((num, idx) => (
                <MiniNumber key={idx} $hasValue={num !== null}>
                  {num ?? '?'}
                </MiniNumber>
              ))}
            </SelectedNumbersRow>
            {selectedNumbers.length > 0 && (
              <ClearButton onClick={handleClear} title="Clear all">
                <X size={16} />
              </ClearButton>
            )}
          </SummaryBar>
        </SelectorCard>
      </GridContainer>
    );
  }
);
